import { Box, Circle, Flex, Stack } from '@chakra-ui/react';
import { Step, useAdminAssignmentsStore } from '../state/adminAssignmentsStore';
import { createRef, forwardRef, useEffect, useRef, useState } from 'react';

import { AngleDown } from '@/client/components/icons/ContinuIcons';

type PreviewContainerProps = {
  step: Step;
  styles: {
    borderColor: string;
    boxShadow?: string | undefined;
  };
  isPastIndex: boolean;
  onStepClick?: () => void;
};

const PreviewContainer = forwardRef<HTMLDivElement, PreviewContainerProps>(
  ({ step, styles, isPastIndex, onStepClick }, ref) => {
    const [opacity, setOpacity] = useState(1);

    useEffect(() => {
      if (!ref || typeof ref === 'function') return;

      const currentRef = ref.current;
      if (!currentRef) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          // Get distance from top of viewport
          const topDistance = entry.boundingClientRect.top;

          // Calculate opacity based on scroll position
          // Start fading at 150px from top, fully faded by 0px
          if (topDistance <= 150 && topDistance >= 0) {
            const op = topDistance / 150; // Linear scale from 0 to 1
            setOpacity(Math.max(0.2, op)); // Don't go below 0.2 opacity
          } else if (topDistance > 150) {
            setOpacity(1); // Fully visible when below threshold
          }
        },
        {
          rootMargin: '-150px 0px 0px 0px',
          threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
        },
      );

      observer.observe(currentRef);

      return () => observer.disconnect();
    }, [ref, step.value]);

    return (
      <Stack
        ref={ref}
        spacing={16}
        opacity={opacity}
        scrollMarginTop={40}
        position="relative"
        transform="translateZ(0)"
        paddingTop={4}
        paddingBottom={4}
      >
        <Box
          id={step.value}
          backgroundColor="white"
          border="2px solid"
          borderRadius="24px"
          sx={styles}
          cursor={isPastIndex ? 'pointer' : 'default'}
          onClick={isPastIndex ? onStepClick : undefined}
          _hover={
            isPastIndex
              ? { transform: 'translateY(-2px)', transition: 'transform 0.2s' }
              : undefined
          }
        >
          {step.previewComponent}
        </Box>

        {step.value !== 'notifications' && (
          <Flex justifyContent="center" marginY={-62}>
            <Flex direction="column" alignItems="center" position="relative">
              <Circle
                position="absolute"
                top={-3}
                backgroundColor="white"
                border="3px solid"
                borderColor={isPastIndex ? '#46D29D' : 'neutral.100'}
                size={4}
              />

              <Box
                border="1px solid"
                borderColor={isPastIndex ? '#46D29D' : 'neutral.100'}
                height={50}
                width={0}
              />

              <AngleDown
                position="absolute"
                bottom={-2}
                color={isPastIndex ? '#46D29D' : 'neutral.100'}
              />
            </Flex>
          </Flex>
        )}
      </Stack>
    );
  },
);

PreviewContainer.displayName = 'PreviewContainer';

type AssignmentBuilderPreviewProps = {
  containerRef: React.RefObject<HTMLDivElement>;
};

export default function AssignmentBuilderPreview({ containerRef }: AssignmentBuilderPreviewProps) {
  const { steps, stepIndex, previewStatusMap, setStepIndex } = useAdminAssignmentsStore();
  const prevStepIndex = useRef(stepIndex);

  // * Remove the details and publish steps from the preview since they don't have a preview card
  const stepsWithoutDetailsAndPublish = steps.filter(
    (s) => s.value !== 'details' && s.value !== 'publish',
  );

  // * Create a scroll ref for each step
  const refs = stepsWithoutDetailsAndPublish.reduce(
    (acc: { [key: string]: React.RefObject<HTMLDivElement> }, value) => {
      acc[value.value] = createRef<HTMLDivElement>();
      return acc;
    },
    {},
  );

  // * Handles scrolling to active step ref
  const handleClick = (value: string) => {
    const element = refs[value].current;
    if (element && containerRef.current) {
      containerRef.current.scrollTo({
        top: element.offsetTop,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const adjustedIndex = stepsWithoutDetailsAndPublish.findIndex(
      (s) => s.value === steps[stepIndex].value,
    );

    if (adjustedIndex !== -1) {
      const element = refs[stepsWithoutDetailsAndPublish[adjustedIndex].value].current;
      if (element && containerRef.current) {
        containerRef.current.scrollTo({
          top: element.offsetTop,
          behavior: 'smooth',
        });
      }
    }
  }, [stepIndex, steps, stepsWithoutDetailsAndPublish]);

  useEffect(() => {
    const adjustedIndex = stepsWithoutDetailsAndPublish.findIndex(
      (s) => s.value === steps[stepIndex].value,
    );
    if (adjustedIndex !== -1) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        handleClick(stepsWithoutDetailsAndPublish[adjustedIndex].value);
      }, 0);
    }
    prevStepIndex.current = stepIndex;
  }, [stepIndex, steps, stepsWithoutDetailsAndPublish]);

  const statusStyles: {
    [key: string]: { borderColor: string; boxShadow?: string; transition: string };
  } = {
    completed: {
      borderColor: 'transparent',
      transition: 'all 0.5s ease-in-out',
    },
    inProgress: {
      borderColor: 'warmNeutral.300',
      boxShadow: `0px 0px 4px 8px var(--chakra-colors-warmNeutral-100)`,
      transition: 'all 0.5s ease-in-out',
    },
    notStarted: {
      borderColor: 'transparent',
      transition: 'all 0.5s ease-in-out',
    },
  };

  return (
    <Box
      ref={containerRef}
      position="relative"
      height="full"
      sx={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'neutral.200',
          borderRadius: '24px',
        },
      }}
    >
      <Stack
        spacing={10}
        paddingX={4}
        position="relative"
        transform="translateZ(0)"
        paddingBottom={64}
      >
        {stepsWithoutDetailsAndPublish.map((step) => {
          const isPastIndex = steps.findIndex((s) => s.value === step.value) < stepIndex;
          const stepIndexInFullSteps = steps.findIndex((s) => s.value === step.value);

          return (
            <PreviewContainer
              key={step.value}
              ref={refs[step.value]}
              step={step}
              styles={statusStyles[previewStatusMap[step.value]]}
              isPastIndex={isPastIndex}
              onStepClick={isPastIndex ? () => setStepIndex(stepIndexInFullSteps) : undefined}
            />
          );
        })}
      </Stack>
    </Box>
  );
}
